import {batch} from 'react-redux';
import {FastRobotErrorcode, TrayType, ViewMode} from '../../../constants/dataEnum';
import {WizardFlag} from '../../../constants/SetupWizardEnum';
import {WellLabel} from '../../../contents/PlateTypeEditor/PlateTypeEditorEnum';
import {hideLoading, showLoading} from '../../actions/appStateAction';
import {
  clearWizardFlag,
  setCurrentStepData,
  setWizardAllowedNext,
  setWizardFlag,
} from '../../actions/wizardStateAction';
import {checkProtocolExecutorAndShowBusyModal, showModalUnsupportedTray} from '../commonModalBusiness';
// eslint-disable-next-line import/no-cycle
import {fastCommonActuatorAPIErrorHandler} from '../errorHandlerBusiness';
import {
  alignToDeckCaddy,
  alignToDeckCaddyAfterSetMarkerA1,
  alignToDeckInsertWithOffset,
  alignXYZ,
  calibrateCollision,
  checkTipPickUp,
  ejectTray,
  getCurrentPosition,
  isTouchProbeExpanded,
  moveToIdlePosition,
  moveVerticalCarrierExt,
  preMoveCalibrateScanStuckTips,
  resetDataMarkerPosition,
  safeTouchProbeExtend,
  safeTouchProbeRetract,
  scanYawSequence,
  setMarkerPositionAfterUpdateA1,
  updateA1Offset,
} from '../instrumentBusiness';
import {checkIsInstrumentIdle} from '../instrumentStatusBusiness';
import {showModalAutoCalibrationConfirmation} from '../modalStateBusinessAction';
import {proceedMeasurement} from '../plateTypeEditorBusiness';
import {fastRecovery} from '../recoveryBusiness';
import {saveState} from '../stateBusiness';
import {checkAndSetIsTrayAttached} from '../trayBusiness';
// eslint-disable-next-line import/namespace
import {onAutoCalibration} from '../trayCalibrationBusiness';
import {showMissingTrayModal} from '../trayModalBusiness';
import {getSetupWizardCurrentStepData, getSetupWizardFlagMessage} from './commonSetupWizardBusiness';
import {showRemoveStuckTipAndReturnA1Position, showReplaceCaddyModal} from './setupWizardModalBusiness';
import {setButtonContinueAccessBackward} from './setupWizardNavigationBusiness';
import {checkAndHandleTraySuitability} from '../trayCheckingBusiness';

export const checkIsInstrumentIdleAndShowModal = () => async (dispatch, getState) => {
  const {appState} = getState();
  const {loading} = appState;

  const isInstrumentIdle = await dispatch(checkIsInstrumentIdle(true));

  if (!isInstrumentIdle) {
    dispatch(checkProtocolExecutorAndShowBusyModal());
    if (loading.show) dispatch(hideLoading());
  }

  return isInstrumentIdle;
};

export const setupWizardMissingTrayAction = () => async (dispatch) => {
  const stepData = dispatch(getSetupWizardCurrentStepData());

  if (stepData?.currentSubstep === 1 || !stepData?.currentSubstep) return;

  await dispatch(fastRecovery());
  batch(() => {
    // if tray is missing, bring the user to the start of the step
    dispatch(setButtonContinueAccessBackward());
    dispatch(setCurrentStepData({currentSubstep: 1}));
  });

  dispatch(saveState());
};

export const checkTraySuitabilitySetupWizard = () => async (dispatch) =>
  dispatch(checkAndHandleTraySuitability(() => dispatch(setupWizardMissingTrayAction())));

const defaultWizardAPICallResponseOptions = {
  successCallback: () => {},
  alternateCallback: () => {},
  isChangeFlag: true,
  isStepProcessed: true,
  isCallCommonActuatorHandler: true,
  isSetContinueButtonAccess: true,
  isHideLoading: true,
};

export const handleDefaultWizardAPICallResponse =
  (response, opt = defaultWizardAPICallResponseOptions) =>
  async (dispatch) => {
    const {
      successCallback,
      alternateCallback,
      isChangeFlag,
      isStepProcessed,
      isCallCommonActuatorHandler,
      isSetContinueButtonAccess,
      isHideLoading,
    } = {...defaultWizardAPICallResponseOptions, ...opt} || {};

    const responseToCheck = response?.result || response;
    const isResponseSuccess =
      responseToCheck === FastRobotErrorcode.NoError || (typeof responseToCheck !== 'string' && responseToCheck);

    // step is not skippable when success because the continue button is already enabled to move forward instead of the skip button
    const isStepSkippable = !isResponseSuccess;
    const isProcessStep = isResponseSuccess ? isStepProcessed : false;
    const isCallErrorHandler = isCallCommonActuatorHandler && !isResponseSuccess;

    const wizardFlag = isResponseSuccess ? WizardFlag.SUCCESS : WizardFlag.ERROR;

    if (isHideLoading) dispatch(hideLoading());

    if (isSetContinueButtonAccess) {
      dispatch(setWizardAllowedNext(isResponseSuccess));
    }

    if (isChangeFlag) {
      dispatch(setWizardFlag(wizardFlag));
      dispatch(setCurrentStepData({isSkippable: isStepSkippable, isStepProcessed: isProcessStep}));
    }

    if (isCallErrorHandler) {
      const addedAlternateCallback = async () => {
        if (responseToCheck === FastRobotErrorcode.MissingTray) {
          await dispatch(setupWizardMissingTrayAction());
        }

        alternateCallback();
      };

      dispatch(fastCommonActuatorAPIErrorHandler(responseToCheck, successCallback, addedAlternateCallback));
    }

    await dispatch(saveState());

    return isResponseSuccess;
  };

export const handleDefaultWizardAPICall =
  (
    func,
    // eslint-disable-next-line default-param-last
    param = null,
    // eslint-disable-next-line default-param-last
    isShowLoading = true,
    // eslint-disable-next-line default-param-last
    setAllowedNext = false,
    // eslint-disable-next-line default-param-last
    changeResponseFlag = false,
    callbackResponse,
    alternateCallbackResponse,
    isStepProcessed = true,
  ) =>
  async (dispatch) => {
    const loadingMsg = dispatch(getSetupWizardFlagMessage(WizardFlag.LOADING));
    if (isShowLoading) dispatch(showLoading(loadingMsg));

    const isInstrumentIdle = await dispatch(checkIsInstrumentIdleAndShowModal());
    const isTraySuitable = await dispatch(checkTraySuitabilitySetupWizard());

    if (!isInstrumentIdle || !isTraySuitable) return false;

    let result;

    if (param) {
      let paramFunction = param;

      if (!Array.isArray(param)) {
        paramFunction = [param];
      }

      result = await dispatch(func(...paramFunction));
    } else result = await dispatch(func());

    if (isShowLoading) dispatch(hideLoading());

    const responseHandlerOptions = {
      isChangeFlag: changeResponseFlag,
      isStepProcessed,
      successCallback: callbackResponse,
      alternateCallback: alternateCallbackResponse,
      isSetContinueButtonAccess: setAllowedNext,
      isHideLoading: isShowLoading,
    };

    return dispatch(handleDefaultWizardAPICallResponse(result, responseHandlerOptions));
  };

// #region API call with default handler

export const handleCalibrateCollision = () => async (dispatch) => {
  const res = await dispatch(handleDefaultWizardAPICall(calibrateCollision, null, true, true, true));
  const isTrayAttached = await dispatch(checkAndSetIsTrayAttached());

  if (!isTrayAttached) {
    dispatch(showMissingTrayModal(true));
  }

  return res;
};

export const handleAlignA1 = () => async (dispatch, getState) => {
  const {trayCalibrationState} = getState();
  const {trayType} = trayCalibrationState;

  if (trayType !== TrayType.NORMAL_CADDY) {
    dispatch(showModalUnsupportedTray(true, ViewMode.SETUP_WIZARD));
    return false;
  }

  return dispatch(handleDefaultWizardAPICall(alignToDeckCaddy, 1, true));
};

export const handleTouchProbeRetract =
  (isShowLoading = true) =>
  async (dispatch) => {
    const isProbeExtended = await dispatch(isTouchProbeExpanded());
    if (isProbeExtended) {
      return dispatch(handleDefaultWizardAPICall(safeTouchProbeRetract, null, isShowLoading));
    }
    return true;
  };

export const handleMoveToIdlePosition =
  (isShowLoading = true) =>
  async (dispatch) =>
    dispatch(handleDefaultWizardAPICall(moveToIdlePosition, [isShowLoading], isShowLoading));

export const handleMoveVerticalCarrier = () => async (dispatch) =>
  dispatch(handleDefaultWizardAPICall(moveVerticalCarrierExt, [0, 20, 100]));

export const handleEjectTray = () => async (dispatch) => dispatch(handleDefaultWizardAPICall(ejectTray));

export const handleCheckTipPickup = () => async (dispatch, getState) => {
  const {trayCalibrationState} = getState();
  const {trayType} = trayCalibrationState;

  if (trayType !== TrayType.NORMAL_CADDY) {
    dispatch(showModalUnsupportedTray(true, ViewMode.SETUP_WIZARD));
    return false;
  }

  dispatch(showLoading());

  const initialPickTipPosition = await dispatch(getCurrentPosition());
  const {x, y, z} = initialPickTipPosition;

  const errCode = await dispatch(checkTipPickUp());

  const alignToInitialTipPickup = async () => {
    await dispatch(handleDefaultWizardAPICall(alignXYZ, [x, y, z]));
  };

  dispatch(hideLoading());

  if (errCode !== FastRobotErrorcode.NoError) {
    dispatch(
      fastCommonActuatorAPIErrorHandler(errCode, () => dispatch(showReplaceCaddyModal(alignToInitialTipPickup))),
    );
    return false;
  }

  dispatch(showRemoveStuckTipAndReturnA1Position(alignToInitialTipPickup));
  return true;
};

export const handleResetDataMarkerPosition = () => (dispatch) =>
  dispatch(handleDefaultWizardAPICall(resetDataMarkerPosition));

export const handleAlignToDeckCaddyAfterSetMarkerA1 = () => (dispatch) =>
  dispatch(handleDefaultWizardAPICall(alignToDeckCaddyAfterSetMarkerA1));

export const handleUpdateA1Offset = () => async (dispatch, getState) => {
  const {trayCalibrationState} = getState();
  const {trayType} = trayCalibrationState;

  if (trayType !== TrayType.NORMAL_CADDY) {
    dispatch(showModalUnsupportedTray(true, ViewMode.SETUP_WIZARD));
    return false;
  }

  return dispatch(handleDefaultWizardAPICall(updateA1Offset));
};

export const handlePreMoveStuckTipCalibration = () => async (dispatch) =>
  dispatch(handleDefaultWizardAPICall(preMoveCalibrateScanStuckTips));

export const handleTouchProbeExtend =
  (isShowLoading = true) =>
  async (dispatch) => {
    let isProbeExtended = await dispatch(isTouchProbeExpanded());
    if (!isProbeExtended) {
      return dispatch(
        handleDefaultWizardAPICall(
          safeTouchProbeExtend,
          null,
          isShowLoading,
          true,
          false,
          async () => {
            isProbeExtended = await dispatch(isTouchProbeExpanded());
            if (isProbeExtended) {
              dispatch(setWizardAllowedNext(true));
            } else {
              dispatch(handleTouchProbeExtend());
            }
          },
          () => dispatch(setWizardAllowedNext(false)),
          false,
        ),
      );
    }

    dispatch(setWizardAllowedNext(true));
    return true;
  };

export const handleAlignToDeckWithOffset = () => async (dispatch) => {
  const handleRecoverAlignToDeckInsertError = async () => {
    const isProbeExtended = await dispatch(isTouchProbeExpanded());

    if (isProbeExtended) {
      dispatch(alignToDeckInsertWithOffset(2, WellLabel.WELL_96));
      dispatch(setWizardAllowedNext(true));
    }
  };

  return dispatch(
    handleDefaultWizardAPICall(
      alignToDeckInsertWithOffset,
      [2, WellLabel.WELL_96],
      true,
      false,
      false,
      handleRecoverAlignToDeckInsertError,
    ),
  );
};

export const handleProceedWellMeasurement =
  (callback = () => {}) =>
  async (dispatch) => {
    const isTraySuitable = await dispatch(checkTraySuitabilitySetupWizard());

    if (!isTraySuitable) return;

    const isInstrumentIdle = dispatch(checkIsInstrumentIdleAndShowModal());

    if (isInstrumentIdle) {
      await dispatch(
        proceedMeasurement(null, false, (isNoError, errorCode) => {
          if (errorCode === FastRobotErrorcode.MissingTray) {
            dispatch(setupWizardMissingTrayAction());
            return;
          }

          callback(isNoError);
        }),
      );
      return;
    }

    callback(false);
  };

export const handleSWizardAutoCalibration =
  (targetDeck = [0, 1, 2, 5, 4, 3], isResume = false) =>
  async (dispatch) => {
    const isInstrumentIdle = await dispatch(checkIsInstrumentIdleAndShowModal());
    if (!isInstrumentIdle) return;

    const startAutoCalibration = async () => {
      await dispatch(onAutoCalibration(targetDeck, isResume));
    };

    if (isResume) {
      startAutoCalibration();
    } else {
      dispatch(showModalAutoCalibrationConfirmation(startAutoCalibration));
    }
  };

export const handleSWizardAutoCalibrationResponse = (response) => async (dispatch) => {
  dispatch(hideLoading());
  let isNoError;

  if (!response?.isAborted) {
    const {errorCode} = response;
    const handlerOptions = {isCallCommonActuatorHandler: false};

    isNoError = await dispatch(handleDefaultWizardAPICallResponse(errorCode, handlerOptions));

    // if (isNoError) {
    //   const isA1MetadataExists = await dispatch(isA1OffsetMetadataExist());

    //   dispatch(setStepDataA1_OFFSET_CALIBRATION, { isSkippable: isA1MetadataExists }));
    // }
  }

  if ((!isNoError || response?.isAborted) && response?.errorCode !== FastRobotErrorcode.ActuatorEmergencyStop)
    dispatch(clearWizardFlag());

  dispatch(setCurrentStepData({isSkippable: !isNoError, isStepProcessed: isNoError}));
};

// #end region API call with default handler
export const handleSetMarkerPositionAfterUpdateA1 = (callback) => async (dispatch) => {
  dispatch(showLoading());
  const response = await dispatch(setMarkerPositionAfterUpdateA1());
  const isErrorTrayIdNotFound = response.errorCode === FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND;
  dispatch(hideLoading());

  if (response.errorCode && response.errorCode !== FastRobotErrorcode.NoError) {
    const successCallback = () => dispatch(handleSetMarkerPositionAfterUpdateA1(callback));
    const alternateCallback = async () => {
      if (isErrorTrayIdNotFound) {
        await dispatch(handleAlignToDeckCaddyAfterSetMarkerA1());
        callback(true);
      } else callback(false);
    };

    dispatch(fastCommonActuatorAPIErrorHandler(response.errorCode, successCallback, alternateCallback));
  } else if (callback) {
    callback(true);
  }
};

export const yawSequenceErrorHandler = (errorCode, callback) => async (dispatch) => {
  const alternateCallback =
    errorCode === FastRobotErrorcode.TRAY_ID_POSITION_NOT_FOUND &&
    (async () => {
      dispatch(showLoading());
      await dispatch(resetDataMarkerPosition());
      dispatch(hideLoading());
    });

  dispatch(fastCommonActuatorAPIErrorHandler(errorCode, callback, alternateCallback));
};

export const handleScanYawSequence = (callback) => async (dispatch) => {
  dispatch(showLoading());
  const {errorCode, needToUpdateA1} = await dispatch(scanYawSequence());
  dispatch(hideLoading());

  if (errorCode !== FastRobotErrorcode.NoError) dispatch(yawSequenceErrorHandler(errorCode, callback));

  return {errorCode, isA1Calibrated: !needToUpdateA1};
};
